import { useMemo } from 'react'

import { useStorefrontCatalog } from '@/shared/api/generated/storefront-catalog/storefront-catalog'
import { formatUsd } from '@/shared/libs/money'

import type { CatalogBook, CatalogPlan, StorefrontCatalog } from '@/shared/api/models'
import type { CartLineInput } from './CartContext'

/**
 * UI tiers are bundles of cart lines (see docs/checkout/stripe-checkout.md):
 * - single_story   = software(story)
 * - unlimited      = unlimited(story)
 * - unlimited_book = unlimited(story) + physical_book(default preset, unassigned)
 */
export type TierId = 'single_story' | 'unlimited' | 'unlimited_book'

export const TIER_IDS: TierId[] = ['single_story', 'unlimited', 'unlimited_book']

export interface TierOption {
  id: TierId
  name: string
  /** Formatted price, e.g. "$79". Empty while the catalog is loading. */
  price: string
  stickerPrice?: string
  badge?: string
  savingsPercent?: string
  perStory?: string
  features: string[]
  /** False when the backend doesn't sell every product the tier needs. */
  isAvailable: boolean
}

const TIER_COPY: Record<TierId, { name: string; badge?: string }> = {
  single_story: { name: 'Single Story' },
  unlimited: { name: 'Unlimited Stories', badge: 'Popular' },
  unlimited_book: { name: 'Unlimited + Hardcover', badge: 'Gift-Ready' },
}

/** The book bundled with `unlimited_book`: the catalog default, else the first hardcover. */
export function getDefaultBook(catalog: StorefrontCatalog | undefined): CatalogBook | undefined {
  if (!catalog) return undefined

  return catalog.books.find((book) => book.preset_id === catalog.default_book_preset_id)
    ?? catalog.books.find((book) => book.cover_type === 'hardcover')
    ?? catalog.books[0]
}

function toNumber(value: string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null

  const parsed = parseFloat(value)

  return Number.isNaN(parsed) ? null : parsed
}

function savingsLabel(price: number | null, compareAt: number | null): string | undefined {
  if (price === null || compareAt === null || compareAt <= price) return undefined

  return `Save ${Math.round((1 - price / compareAt) * 100)}%`
}

function planFeatures(tier: TierId, software: CatalogPlan | null | undefined): string[] {
  if (tier === 'single_story') {
    const credits = software?.credits_amount

    return [
      'Unlock your story + software access',
      credits ? `${credits} bonus credits (1 story)` : 'Bonus credits for 1 story',
    ]
  }

  if (tier === 'unlimited') {
    return [
      'Software access included',
      'Unlimited story creation',
      'Fair use: 50 stories/month',
    ]
  }

  return [
    'Everything in Unlimited Stories',
    'Hardcover book shipped to your door',
    'Assign to any finished story',
  ]
}

/** Build the tier cards from the catalog. Works with an undefined catalog (prices blank). */
export function buildTierOptions(catalog: StorefrontCatalog | undefined): TierOption[] {
  const software = catalog?.software ?? null
  const unlimited = catalog?.unlimited ?? null
  const book = getDefaultBook(catalog)

  return TIER_IDS.map((id) => {
    let price: number | null = null
    let compareAt: number | null = null
    let isAvailable = false

    if (id === 'single_story') {
      price = toNumber(software?.price)
      compareAt = toNumber(software?.compare_at_price)
      isAvailable = !!software
    } else if (id === 'unlimited') {
      price = toNumber(unlimited?.price)
      compareAt = toNumber(unlimited?.compare_at_price)
      isAvailable = !!unlimited
    } else {
      const unlimitedPrice = toNumber(unlimited?.price)
      const bookPrice = toNumber(book?.price)

      if (unlimitedPrice !== null && bookPrice !== null) {
        price = unlimitedPrice + bookPrice
        // Anchor = each part's compare-at (or its price when it has none).
        compareAt = (toNumber(unlimited?.compare_at_price) ?? unlimitedPrice)
          + (toNumber(book?.compare_at_price) ?? bookPrice)
      }

      isAvailable = !!unlimited && !!book
    }

    const formattedPrice = price === null ? '' : formatUsd(price)

    return {
      id,
      name: TIER_COPY[id].name,
      badge: TIER_COPY[id].badge,
      price: formattedPrice,
      stickerPrice: compareAt !== null && price !== null && compareAt > price ? formatUsd(compareAt) : undefined,
      savingsPercent: savingsLabel(price, compareAt),
      perStory: id === 'single_story' && formattedPrice ? `${formattedPrice}/story` : undefined,
      features: planFeatures(id, software),
      isAvailable,
    }
  })
}

/** Tier cards priced from the live catalog. */
export function useTierOptions() {
  const { data: catalog, isLoading } = useStorefrontCatalog({
    query: { staleTime: 5 * 60 * 1000 },
  })

  const tiers = useMemo(() => buildTierOptions(catalog), [catalog])

  return { tiers, catalog, isLoading }
}

/** Cart lines that make up a tier for the given (preview) story. */
export function tierToLines(
  tier: TierId,
  storyId: number | undefined,
  catalog: StorefrontCatalog | undefined,
): CartLineInput[] {
  const storyRef = storyId ? { story_id: storyId } : {}

  if (tier === 'single_story') {
    return [{ kind: 'software', ...storyRef }]
  }

  if (tier === 'unlimited') {
    return [{ kind: 'unlimited', ...storyRef }]
  }

  const book = getDefaultBook(catalog)

  // The bundle's book is tagged so plan switches only ever touch that copy.
  return [
    { kind: 'unlimited', ...storyRef },
    { kind: 'physical_book', ...(book ? { preset_id: book.preset_id } : {}), quantity: 1, fromBundle: true },
  ]
}
